/**
 * Optional-asset preflight. The game has no required external assets: this
 * manager exists so an optional feature (head tracking) can report a missing
 * model instead of failing with a blank screen.
 */
export interface AssetReport {
  id: string;
  url: string;
  ok: boolean;
  detail: string;
}

export class AssetManager {
  private readonly reports = new Map<string, AssetReport>();

  constructor(
    // Call the global through an arrow so the browser's `fetch` gets `window`
    // as its receiver. Storing the bare function and invoking it as
    // `this.fetchImpl(...)` passes the AssetManager instead, which Chrome
    // rejects with "Illegal invocation".
    private readonly fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
  ) {}

  get list(): AssetReport[] {
    return [...this.reports.values()];
  }

  /** Check that a URL is reachable; the result is cached per id. */
  async probe(id: string, url: string): Promise<boolean> {
    const cached = this.reports.get(id);
    if (cached) return cached.ok;

    try {
      const response = await this.fetchImpl(url, { method: 'HEAD', cache: 'force-cache' });
      if (!response.ok) {
        this.reports.set(id, {
          id,
          url,
          ok: false,
          detail: `HTTP ${response.status} ${response.statusText}`.trim(),
        });
        return false;
      }
      this.reports.set(id, { id, url, ok: true, detail: 'ok' });
      return true;
    } catch (error) {
      this.reports.set(id, {
        id,
        url,
        ok: false,
        detail: error instanceof Error ? error.message : 'network error',
      });
      return false;
    }
  }
}

/** Replace the boot overlay with an actionable error instead of a blank page. */
export function showBootError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  let boot = document.getElementById('boot');
  if (!boot) {
    boot = document.createElement('div');
    boot.id = 'boot';
    document.body.append(boot);
  }
  boot.replaceChildren();
  const panel = document.createElement('div');
  panel.className = 'boot-panel boot-error';
  const title = document.createElement('h1');
  title.textContent = 'MAGNETIC HOUND COULD NOT START';
  const detail = document.createElement('p');
  detail.textContent = message;
  const hint = document.createElement('p');
  hint.textContent = 'Reload the page. If the problem persists, check the browser console.';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Reload';
  reload.addEventListener('click', () => window.location.reload());
  panel.append(title, detail, hint, reload);
  boot.append(panel);

  // Keep the error visible even if the regular boot screen had a transition.
  boot.classList.remove('hidden');
  boot.dataset.state = 'error';
}

/**
 * Report a lost WebGL context.
 *
 * The renderer cannot recover by itself, so the player is told what happened
 * and offered a reload. See `docs/ARCHITECTURE.md` §13.
 */
export function showContextLostError(): void {
  showBootError(
    new Error(
      'The graphics context was lost. This usually means the GPU was reset or the tab ran out of memory.',
    ),
  );
}

export function hideBootScreen(): void {
  const boot = document.getElementById('boot');
  if (boot) boot.classList.add('hidden');
}
